import React, { useState, useEffect, useCallback } from 'react';
import { X, Trophy, ChevronDown, ChevronUp } from 'lucide-react';
import { api, inr } from '../api';
import { toast } from 'sonner';

const istToday = () => new Date(Date.now() + 5.5 * 3600000).toISOString().slice(0, 10);

function presetRange(preset) {
  const today = istToday();
  const ym = today.slice(0, 7);
  if (preset === 'This Month') return [ym + '-01', today];
  if (preset === 'Last Month') {
    const d = new Date(ym + '-01T00:00:00Z');
    d.setUTCMonth(d.getUTCMonth() - 1);
    const lm = d.toISOString().slice(0, 7);
    const end = new Date(ym + '-01T00:00:00Z');
    end.setUTCDate(0);
    return [lm + '-01', end.toISOString().slice(0, 10)];
  }
  if (preset === 'Last 3 Months') {
    const d = new Date(ym + '-01T00:00:00Z');
    d.setUTCMonth(d.getUTCMonth() - 2);
    return [d.toISOString().slice(0, 10), today];
  }
  return ['', ''];
}

const PRESETS = ['This Month', 'Last Month', 'Last 3 Months', 'All Time'];

export default function BrownieTallyModal({ onClose }) {
  const [preset, setPreset] = useState('This Month');
  const [[from, to], setRange] = useState(presetRange('This Month'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async (f, t) => {
    setLoading(true);
    try {
      const params = {};
      if (f) params.date_from = f;
      if (t) params.date_to = t;
      const r = await api.get('/admin/brownie-tally', { params });
      setData(r.data);
    } catch {
      toast.error('Failed to load points tally');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(from, to); }, []); // eslint-disable-line

  const applyPreset = (p) => {
    setPreset(p);
    const r = presetRange(p);
    setRange(r);
    load(r[0], r[1]);
  };

  const fmtDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-3 md:p-6" onClick={onClose} data-testid="brownie-tally-overlay">
      <div className="bg-white rounded-2xl sticky-shadow w-full max-w-2xl max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="brownie-tally-modal">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100">
          <h3 className="font-display font-extrabold text-lg text-slate-900 flex items-center gap-2">
            <Trophy size={18} className="text-amber-500" /> Brownie Points Tally
          </h3>
          <button data-testid="brownie-tally-close" onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500"><X size={18} /></button>
        </div>

        <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
          {PRESETS.map((p) => (
            <button key={p} data-testid={`tally-filter-${p.toLowerCase().replace(/ /g, '-')}`}
              onClick={() => applyPreset(p)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-bold border-2 transition-colors ${preset === p ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
              {p}
            </button>
          ))}
          <div className="flex items-center gap-1.5 ml-auto">
            <input data-testid="tally-filter-from" type="date" value={from} onChange={(e) => setRange([e.target.value, to])}
              className="bg-white border border-slate-300 rounded-lg px-2 py-1 text-[11px] focus:outline-none" />
            <span className="text-[11px] text-slate-400 font-bold">→</span>
            <input data-testid="tally-filter-to" type="date" value={to} onChange={(e) => setRange([from, e.target.value])}
              className="bg-white border border-slate-300 rounded-lg px-2 py-1 text-[11px] focus:outline-none" />
            <button data-testid="tally-filter-apply" onClick={() => { setPreset('Custom'); load(from, to); }}
              className="bg-blue-600 text-white rounded-lg px-3 py-1.5 text-[11px] font-bold hover:bg-blue-500">Apply</button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scroll-thin px-5 py-3 space-y-2" data-testid="brownie-tally-list">
          {loading && <p className="font-hand text-xl text-slate-400 text-center py-8">Counting brownies…</p>}
          {!loading && data?.rows?.map((r, idx) => {
            const pos = r.balance >= 0;
            const isOpen = expanded === r.agent_id;
            return (
              <div key={r.agent_id} className="border-2 border-slate-200 rounded-xl overflow-hidden" data-testid={`tally-row-${r.agent_id}`}>
                <button className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors text-left"
                  data-testid={`tally-expand-${r.agent_id}`}
                  onClick={() => setExpanded(isOpen ? null : r.agent_id)}>
                  <span className="font-display font-extrabold text-sm text-slate-300 w-5 shrink-0">#{idx + 1}</span>
                  <span className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-extrabold text-slate-800 border-2 border-white shadow-sm shrink-0" style={{ background: r.avatar_color }}>
                    {r.name.split(' ').map((w) => w[0]).join('')}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-slate-900 truncate">{r.name}</p>
                    <p className="text-[10px] text-slate-400">
                      {r.days_present} day{r.days_present === 1 ? '' : 's'} in · sales {inr(r.sales_total)}
                      {r.target > 0 ? ` · target ${inr(r.target)}/day @ ${r.brownie_pct}%` : ' · no target set'}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`font-display font-extrabold text-base ${pos ? 'text-emerald-600' : 'text-red-500'}`} data-testid={`tally-points-${r.agent_id}`}>
                      {pos ? '🍫 +' : '🍩 '}{Math.round(r.points).toLocaleString('en-IN')} pts
                    </p>
                    {r.redeemed > 0 && <p className="text-[10px] text-violet-600 font-bold">−{r.redeemed} redeemed · bal {Math.round(r.balance).toLocaleString('en-IN')}</p>}
                  </div>
                  {isOpen ? <ChevronUp size={14} className="text-slate-400 shrink-0" /> : <ChevronDown size={14} className="text-slate-400 shrink-0" />}
                </button>
                {isOpen && (
                  <div className="border-t border-slate-100 bg-amber-50/40 px-3 py-2 max-h-52 overflow-y-auto scroll-thin" data-testid={`tally-daily-${r.agent_id}`}>
                    {r.daily.length === 0 && <p className="font-hand text-base text-slate-400 text-center py-2">no active days in this period</p>}
                    {r.daily.map((d) => (
                      <div key={d.date} className="flex items-center justify-between text-[11px] py-1 border-b border-amber-100/60 last:border-0">
                        <span className="font-bold text-slate-600 w-16">{fmtDate(d.date)}</span>
                        <span className="text-slate-500">sales {inr(d.sales)}</span>
                        <span className={`font-extrabold ${d.points >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {d.points >= 0 ? '+' : ''}{d.points.toLocaleString('en-IN')} pts
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
